import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Card, TextField, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, Grid, MenuItem, InputAdornment, CircularProgress,
  LinearProgress, Tabs, Tab, Alert, Tooltip
} from '@mui/material';
import { Add, Search, Warning, Refresh, Close, TrendingDown } from '@mui/icons-material';
import api from '../../api/axios';
import toast from 'react-hot-toast';

const categories = ['medicine', 'equipment', 'consumable', 'surgical', 'diagnostic', 'other'];

const defaultForm = {
  name: '', category: 'medicine', quantity: '', minimumStock: '10',
  maximumStock: '1000', costPrice: '', sellingPrice: '', unit: 'units',
  manufacturer: '', batchNumber: '', expiryDate: '', storageLocation: '',
  'supplier.name': '', 'supplier.contact': ''
};

const categoryColors: Record<string, { bg: string; color: string }> = {
  medicine:    { bg: '#eff6ff', color: '#1d4ed8' },
  equipment:   { bg: '#f0fdfa', color: '#0d9488' },
  consumable:  { bg: '#fdf4ff', color: '#a855f7' },
  surgical:    { bg: '#fff7ed', color: '#c2410c' },
  diagnostic:  { bg: '#fef9c3', color: '#a16207' },
  other:       { bg: '#f1f5f9', color: '#475569' },
};

const Inventory = () => {
  const [items,       setItems]       = useState<any[]>([]);
  const [loading,     setLoading]     = useState(true);
  const [search,      setSearch]      = useState('');
  const [tab,         setTab]         = useState(0);
  const [open,        setOpen]        = useState(false);
  const [restockItem, setRestockItem] = useState<any>(null);
  const [restockQty,  setRestockQty]  = useState('');
  const [form,        setForm]        = useState<any>(defaultForm);
  const [total,       setTotal]       = useState(0);
  const [lowCount,    setLowCount]    = useState(0);
  const [submitting,  setSubmitting]  = useState(false);
  const [error,       setError]       = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = { limit: 200 };
      if (search)  params.search   = search;
      if (tab === 1) params.lowStock = 'true';
      if (tab > 1)   params.category = categories[tab - 2];

      const [itemsRes, lowRes] = await Promise.all([
        api.get('/inventory', { params }),
        api.get('/inventory/alerts/low-stock').catch(() => ({ data: { count: 0 } }))
      ]);

      setItems(itemsRes.data.data   || []);
      setTotal(itemsRes.data.total  || 0);
      setLowCount(lowRes.data.count || 0);
    } catch (e: any) {
      toast.error('Failed to load inventory');
    } finally {
      setLoading(false);
    }
  }, [search, tab]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    if (!form.name) { setError('Item name is required'); return; }
    setError(''); setSubmitting(true);
    try {
      await api.post('/inventory', {
        name: form.name, category: form.category,
        quantity: Number(form.quantity) || 0,
        minimumStock: Number(form.minimumStock) || 10,
        maximumStock: Number(form.maximumStock) || 1000,
        costPrice: Number(form.costPrice) || 0,
        sellingPrice: Number(form.sellingPrice) || 0,
        unit: form.unit, manufacturer: form.manufacturer,
        batchNumber: form.batchNumber, storageLocation: form.storageLocation,
        expiryDate: form.expiryDate || undefined,
        supplier: { name: form['supplier.name'], contact: form['supplier.contact'] }
      });
      toast.success('Item added to inventory');
      setOpen(false);
      setForm(defaultForm);
      load();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to add item');
    } finally { setSubmitting(false); }
  };

  const handleRestock = async () => {
    if (!restockQty || Number(restockQty) <= 0) { toast.error('Enter valid quantity'); return; }
    try {
      await api.post(`/inventory/${restockItem._id}/restock`, { quantity: Number(restockQty) });
      toast.success(`+${restockQty} ${restockItem.unit} added`);
      setRestockItem(null);
      setRestockQty('');
      load();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Restock failed');
    }
  };

  const set = (k: string, v: any) => setForm((f: any) => ({ ...f, [k]: v }));

  const stockPct = (item: any) => Math.min((item.quantity / (item.maximumStock || 100)) * 100, 100);
  const stockColor = (item: any) => {
    if (item.quantity === 0) return '#ef4444';
    if (item.quantity <= item.minimumStock) return '#f97316';
    return '#10b981';
  };

  const isExpired = (date?: string) => date && new Date(date) < new Date();
  const expiresSoon = (date?: string) => {
    if (!date) return false;
    const d = new Date(date);
    const diff = (d.getTime() - Date.now()) / 86400000;
    return diff > 0 && diff < 30;
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a' }}>Inventory</Typography>
          <Typography sx={{ color: '#64748b', fontSize: 14, mt: 0.3 }}>
            {total} items tracked
            {lowCount > 0 && (
              <Chip label={`${lowCount} low stock`} size="small" icon={<Warning sx={{ fontSize: '12px !important' }} />}
                sx={{ ml: 1, bgcolor: '#fef2f2', color: '#b91c1c', fontSize: 11, height: 20 }} />
            )}
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={() => { setOpen(true); setError(''); setForm(defaultForm); }}
          sx={{ borderRadius: 2.5, background: 'linear-gradient(135deg,#0d9488,#0f766e)', textTransform: 'none', fontWeight: 600, px: 2.5, boxShadow: '0 4px 14px rgba(13,148,136,0.35)' }}>
          Add Item
        </Button>
      </Box>

      {/* Search */}
      <Card elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 3, p: 2, mb: 2 }}>
        <TextField fullWidth size="small" placeholder="Search by name, manufacturer..."
          value={search} onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ color: '#94a3b8', fontSize: 18 }} /></InputAdornment> }}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2.5, bgcolor: '#f8fafc' } }} />
      </Card>

      {/* Tabs */}
      <Box sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => { setTab(v); }}
          sx={{ '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: 13, minHeight: 40, px: 2 },
               '& .Mui-selected': { color: '#0d9488' }, '& .MuiTabs-indicator': { bgcolor: '#0d9488' } }}>
          <Tab label="All Items" />
          <Tab label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Warning sx={{ fontSize: 15, color: tab === 1 ? '#0d9488' : '#f97316' }} />
              Low Stock {lowCount > 0 && <Chip label={lowCount} size="small" sx={{ height: 16, fontSize: 10, bgcolor: '#fef2f2', color: '#b91c1c', ml: 0.5 }} />}
            </Box>
          } />
          {categories.map(c => <Tab key={c} label={c.charAt(0).toUpperCase() + c.slice(1)} />)}
        </Tabs>
      </Box>

      {/* Table */}
      <Card elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 3, overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress sx={{ color: '#0d9488' }} /></Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                  {['Item Name', 'Category', 'Stock Level', 'Quantity', 'Cost', 'Selling Price', 'Expiry', 'Location', 'Actions'].map(h => (
                    <TableCell key={h} sx={{ fontWeight: 700, color: '#374151', py: 1.5, fontSize: 12, borderBottom: '2px solid #e2e8f0', whiteSpace: 'nowrap' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map(item => {
                  const cc = categoryColors[item.category] || categoryColors.other;
                  const expired = isExpired(item.expiryDate);
                  const soon    = expiresSoon(item.expiryDate);
                  return (
                    <TableRow key={item._id} hover sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                      <TableCell>
                        <Typography sx={{ fontWeight: 600, fontSize: 13, color: '#0f172a' }}>{item.name}</Typography>
                        {item.manufacturer && <Typography sx={{ fontSize: 11, color: '#94a3b8' }}>{item.manufacturer}</Typography>}
                        {item.batchNumber   && <Typography sx={{ fontSize: 10, color: '#cbd5e1' }}>Batch: {item.batchNumber}</Typography>}
                      </TableCell>
                      <TableCell>
                        <Chip label={item.category} size="small"
                          sx={{ bgcolor: cc.bg, color: cc.color, fontSize: 11, textTransform: 'capitalize', fontWeight: 600 }} />
                      </TableCell>
                      <TableCell sx={{ minWidth: 130 }}>
                        <LinearProgress variant="determinate" value={stockPct(item)}
                          sx={{ height: 6, borderRadius: 3, bgcolor: '#f1f5f9', mb: 0.5,
                               '& .MuiLinearProgress-bar': { bgcolor: stockColor(item), borderRadius: 3 } }} />
                        {item.quantity <= item.minimumStock && (
                          <Typography sx={{ fontSize: 10, color: '#f97316', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.3 }}>
                            <TrendingDown sx={{ fontSize: 12 }} /> Low Stock
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontWeight: 700, fontSize: 14, color: stockColor(item) }}>
                          {item.quantity}
                        </Typography>
                        <Typography sx={{ fontSize: 10, color: '#94a3b8' }}>{item.unit}</Typography>
                      </TableCell>
                      <TableCell><Typography sx={{ fontSize: 13 }}>₹{item.costPrice}</Typography></TableCell>
                      <TableCell><Typography sx={{ fontSize: 13, fontWeight: 600 }}>₹{item.sellingPrice}</Typography></TableCell>
                      <TableCell>
                        {item.expiryDate ? (
                          <Chip label={new Date(item.expiryDate).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'2-digit' })}
                            size="small" sx={{ fontSize: 10, bgcolor: expired ? '#fef2f2' : soon ? '#fffbeb' : '#f0fdfa', color: expired ? '#b91c1c' : soon ? '#a16207' : '#0d9488' }} />
                        ) : <Typography sx={{ fontSize: 12, color: '#94a3b8' }}>—</Typography>}
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontSize: 12, color: '#64748b' }}>{item.storageLocation || '—'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Restock">
                          <IconButton size="small" onClick={() => { setRestockItem(item); setRestockQty(''); }}
                            sx={{ color: '#0d9488', '&:hover': { bgcolor: '#f0fdfa' } }}>
                            <Refresh fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 8 }}>
                      <Typography sx={{ color: '#94a3b8' }}>
                        {search ? `No items matching "${search}"` : 'No inventory items found'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      {/* Add Item Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, borderBottom: '1px solid #f1f5f9' }}>
          Add Inventory Item
          <IconButton size="small" onClick={() => setOpen(false)}><Close fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5 }}>
          {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            {[
              { label: 'Item Name *', k: 'name', col: '1 / -1' },
            ].map(({ label, k, col }) => (
              <Box key={k} sx={{ gridColumn: col }}>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#64748b', mb: 0.5 }}>{label}</Typography>
                <TextField fullWidth size="small" value={form[k]} onChange={e => set(k, e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
              </Box>
            ))}
            <Box>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#64748b', mb: 0.5 }}>Category</Typography>
              <TextField select fullWidth size="small" value={form.category} onChange={e => set('category', e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                {categories.map(c => <MenuItem key={c} value={c} sx={{ textTransform: 'capitalize' }}>{c.charAt(0).toUpperCase() + c.slice(1)}</MenuItem>)}
              </TextField>
            </Box>
            <Box>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#64748b', mb: 0.5 }}>Unit</Typography>
              <TextField fullWidth size="small" value={form.unit} onChange={e => set('unit', e.target.value)} placeholder="tablets / vials / units..." sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            </Box>
            {[
              { label: 'Current Quantity', k: 'quantity' },
              { label: 'Minimum Stock Alert', k: 'minimumStock' },
              { label: 'Maximum Stock', k: 'maximumStock' },
              { label: 'Cost Price (₹)', k: 'costPrice' },
              { label: 'Selling Price (₹)', k: 'sellingPrice' },
            ].map(({ label, k }) => (
              <Box key={k}>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#64748b', mb: 0.5 }}>{label}</Typography>
                <TextField fullWidth size="small" type="number" value={form[k]} onChange={e => set(k, e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
              </Box>
            ))}
            <Box>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#64748b', mb: 0.5 }}>Manufacturer</Typography>
              <TextField fullWidth size="small" value={form.manufacturer} onChange={e => set('manufacturer', e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#64748b', mb: 0.5 }}>Batch Number</Typography>
              <TextField fullWidth size="small" value={form.batchNumber} onChange={e => set('batchNumber', e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#64748b', mb: 0.5 }}>Expiry Date</Typography>
              <TextField fullWidth size="small" type="date" value={form.expiryDate} onChange={e => set('expiryDate', e.target.value)} InputLabelProps={{ shrink: true }} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#64748b', mb: 0.5 }}>Storage Location</Typography>
              <TextField fullWidth size="small" value={form.storageLocation} onChange={e => set('storageLocation', e.target.value)} placeholder="e.g. Pharmacy Store A" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#64748b', mb: 0.5 }}>Supplier Name</Typography>
              <TextField fullWidth size="small" value={form['supplier.name']} onChange={e => set('supplier.name', e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#64748b', mb: 0.5 }}>Supplier Contact</Typography>
              <TextField fullWidth size="small" value={form['supplier.contact']} onChange={e => set('supplier.contact', e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #f1f5f9' }}>
          <Button onClick={() => setOpen(false)} sx={{ textTransform: 'none', color: '#64748b' }}>Cancel</Button>
          <Button variant="contained" onClick={handleAdd} disabled={submitting}
            sx={{ borderRadius: 2.5, textTransform: 'none', fontWeight: 600, background: 'linear-gradient(135deg,#0d9488,#0f766e)', px: 3 }}>
            {submitting ? <CircularProgress size={18} sx={{ color: 'white' }} /> : 'Add Item'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Restock Dialog */}
      <Dialog open={!!restockItem} onClose={() => setRestockItem(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700 }}>
          Restock Item
          <IconButton size="small" onClick={() => setRestockItem(null)}><Close fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ p: 1.5, bgcolor: '#f0fdfa', borderRadius: 2, mb: 2 }}>
            <Typography sx={{ fontWeight: 700, fontSize: 14 }}>{restockItem?.name}</Typography>
            <Typography sx={{ fontSize: 12, color: '#64748b' }}>
              Current stock: <strong style={{ color: stockColor(restockItem || {}) }}>{restockItem?.quantity}</strong> {restockItem?.unit}
            </Typography>
            <Typography sx={{ fontSize: 12, color: '#94a3b8' }}>Min: {restockItem?.minimumStock} · Max: {restockItem?.maximumStock}</Typography>
          </Box>
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#64748b', mb: 0.5 }}>Quantity to Add *</Typography>
          <TextField fullWidth type="number" value={restockQty} onChange={e => setRestockQty(e.target.value)}
            placeholder={`e.g. ${restockItem?.minimumStock}`}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
          {restockQty && (
            <Typography sx={{ fontSize: 12, color: '#0d9488', mt: 1 }}>
              New stock will be: <strong>{(restockItem?.quantity || 0) + Number(restockQty)} {restockItem?.unit}</strong>
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setRestockItem(null)} sx={{ textTransform: 'none', color: '#64748b' }}>Cancel</Button>
          <Button variant="contained" onClick={handleRestock}
            sx={{ borderRadius: 2.5, textTransform: 'none', fontWeight: 600, background: 'linear-gradient(135deg,#0d9488,#0f766e)' }}>
            Confirm Restock
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Inventory;
